import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { sparkToast } from '@spark';

import { GlobalContext } from '@/app/providers/globalState';
import { data_from_server_post } from '@/shared/http/client';
import { BEDROCK_REQUIRED_FIELDS } from '@/features/settings/components/providers/BedrockCredentialsForm.jsx';
import { VENDOR_PROVIDERS } from '@/features/settings/components/providers/VendorKeysForm.jsx';

const CLIENT_ID = '40b3a00e282ee2db1338c3bba881b99125fc3ba1d7f5c29232ce1ad63fab1e75';

const INITIAL_PROVIDER_SETTINGS = {
    openai_keys: [],
    anthropic_keys: [],
    google_keys: [],
    deepseek_keys: [],
    jev_keys: [],
    aws_access_key_id: '',
    aws_secret_access_key: '',
    aws_region: '',
    aws_s3_bucket: '',
    aws_role_arn: '',
    vertex: {},
    azure: {},
    platform_cloud_provider: '',
    recommended_provider: null,
};

const REDACTED_PROVIDER_FLAGS = [
    'openai', 'anthropic', 'google', 'deepseek', 'jev', 'bedrock', 'azure', 'vertex',
];

// On SaaS the backend redacts every credential for non-god users and
// answers ``<provider>_configured`` booleans instead (see
// ``organization_settings_route._redact_provider_credentials``). The
// ``has*`` predicates honour those flags so provider counts and the
// onboarding gate still work without the secrets ever reaching the browser.
const isRedactedConfigured = (settings, provider) =>
    settings?.providers_managed_by_platform === true
    && settings?.[`${provider}_configured`] === true;

const hasVendorKeys = (settings, provider) =>
    isRedactedConfigured(settings, provider)
    || (Array.isArray(settings?.[`${provider}_keys`]) && settings[`${provider}_keys`].length > 0);

const hasBedrockCredentials = (settings) =>
    isRedactedConfigured(settings, 'bedrock')
    || BEDROCK_REQUIRED_FIELDS.every((field) => Boolean(settings?.[field]));

export const hasVertexCredentials = (settings) => {
    if (isRedactedConfigured(settings, 'vertex')) return true;
    const vertex = settings?.vertex;
    if (!vertex || typeof vertex !== 'object') return false;
    // Vertex stores no credential — the auth mode is the whole configuration.
    //
    // Must read the *stored* mode only, never ``vertex_effective_auth_mode``.
    // This feeds ``hasAnySavedCredentials``, which gates the first-run
    // onboarding modal: counting the cloud-derived GCP default as a saved
    // credential would silently skip onboarding on every GCP install.
    return Boolean(vertex.vertex_auth_mode);
};

// The provider-tab count, unlike the onboarding gate above, must reflect
// what the gateway actually registered: on a GCP-hosted platform Vertex is
// live through Workload Identity even when nothing was ever saved (the
// panel says "enabled by default"), so the VertexAI tab showing (0) there
// would contradict the panel one click away. ``vertex_effective_auth_mode``
// is the backend-derived resolution — empty off GCP, so non-GCP installs
// keep counting 0 until a mode is explicitly saved.
export const hasEffectiveVertexProvider = (settings) => {
    if (isRedactedConfigured(settings, 'vertex')) return true;
    const vertex = settings?.vertex;
    if (!vertex || typeof vertex !== 'object') return false;
    return Boolean(vertex.vertex_auth_mode || vertex.vertex_effective_auth_mode);
};

export const hasAzureCredentials = (settings) => {
    if (isRedactedConfigured(settings, 'azure')) return true;
    const azure = settings?.azure;
    if (!azure || typeof azure !== 'object') return false;
    // azure_mode defaults to 'openai' even with no credentials — ignore it.
    return Boolean(
        azure.azure_api_key ||
            azure.azure_client_secret ||
            azure.azure_foundry_project_endpoint
    );
};

/**
 * True when the org has at least one persisted model-provider credential.
 */
export function hasAnySavedCredentials(settings) {
    if (!settings) return false;
    if (VENDOR_PROVIDERS.some((p) => hasVendorKeys(settings, p.value))) return true;
    if (hasBedrockCredentials(settings)) return true;
    if (hasVertexCredentials(settings)) return true;
    if (hasAzureCredentials(settings)) return true;
    return false;
}

export function mapOrganizationProviderSettings(result) {
    const next = {
        openai_keys: result.openai_keys || [],
        anthropic_keys: result.anthropic_keys || [],
        google_keys: result.google_keys || [],
        deepseek_keys: result.deepseek_keys || [],
        jev_keys: result.jev_keys || [],
        aws_access_key_id: result.aws_access_key_id || '',
        aws_secret_access_key: result.aws_secret_access_key || '',
        aws_region: result.aws_region || '',
        aws_s3_bucket: result.aws_s3_bucket || '',
        aws_role_arn: result.aws_role_arn || '',
        azure: {
            azure_mode: result.azure_mode || 'openai',
            azure_api_key: result.azure_api_key || '',
            azure_api_base: result.azure_api_base || '',
            azure_tenant_id: result.azure_tenant_id || '',
            azure_client_id: result.azure_client_id || '',
            azure_client_secret: result.azure_client_secret || '',
            azure_foundry_project_endpoint: result.azure_foundry_project_endpoint || '',
            azure_batch_deployments: result.azure_batch_deployments || '',
        },
        vertex: {
            vertex_auth_mode: result.vertex_auth_mode || '',
            vertex_effective_auth_mode: result.vertex_effective_auth_mode || '',
            vertex_project: result.vertex_project || '',
            vertex_location: result.vertex_location || '',
            vertex_gcs_bucket: result.vertex_gcs_bucket || '',
        },
        platform_cloud_provider: result.platform_cloud_provider || '',
        recommended_provider: result.recommended_provider || null,
    };
    if (next.openai_keys.length === 0 && result.openai_key) {
        next.openai_keys = [result.openai_key];
    }
    if (result.providers_managed_by_platform === true) {
        next.providers_managed_by_platform = true;
        for (const provider of REDACTED_PROVIDER_FLAGS) {
            next[`${provider}_configured`] = result[`${provider}_configured`] === true;
        }
    }
    return next;
}

/**
 * One-shot check against the org settings (the source of truth for saved
 * credentials). Used by gates that must not rely on the available-models
 * catalog, which can be empty or stale even when a key is saved.
 * Errors report `false` so callers fall back to the onboarding flow.
 */
export function fetchHasAnySavedCredentials(onResult) {
    data_from_server_post(
        'get_organization_settings',
        CLIENT_ID,
        (result) => onResult(hasAnySavedCredentials(mapOrganizationProviderSettings(result))),
        () => onResult(false)
    );
}

/**
 * Load / save organization model-provider credentials (vendors + cloud).
 * Used by Settings and the first-time Model Providers onboarding modal.
 */
export function useOrganizationProviderSettings({ autoFetch = true } = {}) {
    const { refreshCommercialModels } = useContext(GlobalContext);
    const [settings, setSettings] = useState(INITIAL_PROVIDER_SETTINGS);
    const [isLoading, setIsLoading] = useState(Boolean(autoFetch));

    const fetchSettings = useCallback(() => {
        setIsLoading(true);
        data_from_server_post(
            'get_organization_settings',
            CLIENT_ID,
            (result) => {
                setSettings((prev) => ({ ...prev, ...mapOrganizationProviderSettings(result) }));
                setIsLoading(false);
            },
            () => setIsLoading(false)
        );
    }, []);

    useEffect(() => {
        if (autoFetch) {
            fetchSettings();
        }
    }, [autoFetch, fetchSettings]);

    const updateVendorKeys = useCallback(
        (provider, keys, action) => {
            data_from_server_post(
                'save_llm_keys',
                {
                    client_id: CLIENT_ID,
                    provider,
                    api_keys: keys,
                },
                () => {
                    setSettings((prev) => ({ ...prev, [`${provider}_keys`]: keys }));
                    sparkToast.success(`API keys ${action} successfully`);
                    refreshCommercialModels();
                },
                (error) => {
                    sparkToast.error(`Failed to save API keys: ${error}`);
                }
            );
        },
        [refreshCommercialModels]
    );

    const updateRecommendedProvider = useCallback((provider) => {
        // Optimistic UI so "Marked by <user> DD.MM.YY" appears immediately.
        if (!provider) {
            setSettings((prev) => ({ ...prev, recommended_provider: null }));
        }
        data_from_server_post(
            'save_recommended_provider',
            {
                client_id: CLIENT_ID,
                provider,
            },
            (result) => {
                setSettings((prev) => ({
                    ...prev,
                    recommended_provider: result?.recommended_provider || null,
                }));
                sparkToast.success(
                    provider
                        ? 'Recommended provider saved successfully'
                        : 'Recommended provider cleared successfully'
                );
            },
            (error) => {
                sparkToast.error(`Failed to save recommended provider: ${error}`);
                // Re-fetch to restore server truth after a failed toggle.
            }
        );
    }, []);

    const updateBedrockCredentials = useCallback(
        (fields, action) => {
            data_from_server_post(
                'save_aws_bedrock_credentials',
                {
                    client_id: CLIENT_ID,
                    aws_access_key_id: fields.aws_access_key_id || null,
                    aws_secret_access_key: fields.aws_secret_access_key || null,
                    aws_region: fields.aws_region || null,
                    aws_s3_bucket: fields.aws_s3_bucket || null,
                    aws_role_arn: fields.aws_role_arn || null,
                },
                () => {
                    setSettings((prev) => ({ ...prev, ...fields }));
                    sparkToast.success(`Bedrock credentials ${action} successfully`);
                    refreshCommercialModels();
                },
                (error) => {
                    sparkToast.error(`Failed to save Bedrock credentials: ${error}`);
                }
            );
        },
        [refreshCommercialModels]
    );

    const clearBedrockCredentials = useCallback(() => {
        data_from_server_post(
            'clear_aws_bedrock_credentials',
            {},
            () => {
                setSettings((prev) => ({
                    ...prev,
                    aws_access_key_id: '',
                    aws_secret_access_key: '',
                    aws_region: '',
                    aws_s3_bucket: '',
                    aws_role_arn: '',
                }));
                sparkToast.success('Bedrock credentials cleared successfully');
                refreshCommercialModels();
            },
            (error) => {
                sparkToast.error(`Failed to clear Bedrock credentials: ${error}`);
            }
        );
    }, [refreshCommercialModels]);

    const updateVertexCredentials = useCallback(
        (payload, action) => {
            data_from_server_post(
                'save_vertex_credentials',
                { client_id: CLIENT_ID, ...payload },
                () => {
                    setSettings((prev) => ({ ...prev, vertex: payload }));
                    sparkToast.success(`VertexAI settings ${action} successfully`);
                    refreshCommercialModels();
                },
                (error) => {
                    sparkToast.error(`Failed to save VertexAI settings: ${error}`);
                }
            );
        },
        [refreshCommercialModels]
    );

    const clearVertexCredentials = useCallback(() => {
        data_from_server_post(
            'clear_vertex_credentials',
            {},
            () => {
                // Re-read rather than clearing optimistically: on a GCP
                // platform the provider stays registered by default, and
                // only the server knows that.
                fetchSettings();
                sparkToast.success('VertexAI settings cleared successfully');
                refreshCommercialModels();
            },
            (error) => {
                sparkToast.error(`Failed to clear VertexAI settings: ${error}`);
            }
        );
    }, [fetchSettings, refreshCommercialModels]);

    // Resolves rather than rejects on failure: the form renders the GCP
    // error text inline, which is the only place a missing IAM role
    // becomes visible.
    const testVertexConnection = useCallback(
        () =>
            new Promise((resolve) => {
                data_from_server_post(
                    'test_vertex_connection',
                    {},
                    (result) => resolve(result),
                    (error) => resolve({ success: false, error: String(error) })
                );
            }),
        []
    );

    const updateAzureCredentials = useCallback(
        (fields, action) => {
            data_from_server_post(
                'save_azure_credentials',
                {
                    client_id: CLIENT_ID,
                    azure_mode: fields.azure_mode || null,
                    azure_api_key: fields.azure_api_key || null,
                    azure_api_base: fields.azure_api_base || null,
                    azure_tenant_id: fields.azure_tenant_id || null,
                    azure_client_id: fields.azure_client_id || null,
                    azure_client_secret: fields.azure_client_secret || null,
                    azure_foundry_project_endpoint: fields.azure_foundry_project_endpoint || null,
                    azure_batch_deployments: fields.azure_batch_deployments || null,
                },
                () => {
                    setSettings((prev) => ({ ...prev, azure: fields }));
                    sparkToast.success(`AzureAI credentials ${action} successfully`);
                    refreshCommercialModels();
                },
                (error) => {
                    sparkToast.error(`Failed to save AzureAI credentials: ${error}`);
                }
            );
        },
        [refreshCommercialModels]
    );

    const clearAzureCredentials = useCallback(() => {
        data_from_server_post(
            'clear_azure_credentials',
            {},
            () => {
                setSettings((prev) => ({
                    ...prev,
                    azure: {
                        azure_mode: 'openai',
                        azure_api_key: '',
                        azure_api_base: '',
                        azure_tenant_id: '',
                        azure_client_id: '',
                        azure_client_secret: '',
                        azure_foundry_project_endpoint: '',
                        azure_batch_deployments: '',
                    },
                }));
                sparkToast.success('AzureAI credentials cleared successfully');
                refreshCommercialModels();
            },
            (error) => {
                sparkToast.error(`Failed to clear AzureAI credentials: ${error}`);
            }
        );
    }, [refreshCommercialModels]);

    const hasCredentials = useMemo(
        () => hasAnySavedCredentials(settings),
        [settings]
    );

    return {
        settings,
        isLoading,
        hasCredentials,
        fetchSettings,
        updateVendorKeys,
        updateRecommendedProvider,
        updateBedrockCredentials,
        clearBedrockCredentials,
        updateVertexCredentials,
        clearVertexCredentials,
        testVertexConnection,
        updateAzureCredentials,
        clearAzureCredentials,
    };
}
